import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Moon, Dumbbell, Wine, Coffee, Salad, Shield, ShieldCheck, ShieldAlert, HelpCircle } from 'lucide-react';
import { getDailyLog, saveDailyLog, getProfile, getProductEvents, saveScores } from '../utils/storage';
import { getToday } from '../utils/dateUtils';
import { computeDailyOverview } from '../scoring/edcScoring';

export default function DailyLog() {
    const navigate = useNavigate();
    const today = getToday();
    const profile = useMemo(() => getProfile() || {}, []);

    const [log, setLog] = useState({
        sleepHours: 7,
        exerciseType: 'none',
        exerciseMinutes: 0,
        lowEnergyAvailability: false,
        alcoholDrinks: 0,
        caffeineTiming: 'none',
        upfServings: 0,
        fiberServings: 0,
    });

    useEffect(() => {
        const existing = getDailyLog(today);
        if (existing) setLog(existing);
    }, [today]);

    const updateLog = (field, value) => {
        setLog(prev => ({ ...prev, [field]: value }));
    };

    // Live preview
    const preview = useMemo(() => {
        const events = getProductEvents(today);
        // We're keeping it simple here and just previewing the EDC/Food exposure
        // Behavior mapping to the overall signal level could be added later
        return computeDailyOverview(events, profile);
    }, [today, profile]); // eslint-disable-line

    const handleSave = () => {
        saveDailyLog(today, log);

        // Compute and save daily overview
        const events = getProductEvents(today);
        const overview = computeDailyOverview(events, profile);

        // Save the new overview structure instead of numeric scores
        saveScores(today, {
            ...overview,
            timestamp: new Date().toISOString(),
        });

        navigate('/dashboard');
    };

    let badgeClass = 'signal-unclear';
    let BadgeIcon = HelpCircle;
    if (preview.overallLevel === 'Low Signal') {
        badgeClass = 'signal-low';
        BadgeIcon = ShieldCheck;
    } else if (preview.overallLevel === 'Moderate Signal') {
        badgeClass = 'signal-moderate';
        BadgeIcon = Shield;
    } else if (preview.overallLevel === 'Elevated Signal') {
        badgeClass = 'signal-elevated';
        BadgeIcon = ShieldAlert;
    }

    return (
        <div className="page" style={{ paddingBottom: 100 }}>
            <div className="page-header">
                <h1 className="page-title">Daily Log</h1>
                <p className="page-subtitle">{new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</p>
            </div>

            {/* Overview Preview Band */}
            <div className="overview-hero" style={{ padding: 16, marginBottom: 24 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 8 }}>
                    Current Exposure Signal
                </div>
                <div className={`signal-badge ${badgeClass} large-badge`} style={{ marginBottom: 0 }}>
                    <BadgeIcon size={16} />
                    <span>{preview.overallLevel}</span>
                </div>
            </div>

            {/* Sleep */}
            <div className="quick-input-row">
                <div className="quick-input-left">
                    <div className="quick-input-icon"><Moon size={20} /></div>
                    <div className="quick-input-label">Sleep</div>
                </div>
                <div className="quick-input-right">
                    <span className="compact-value">{log.sleepHours}h</span>
                    <input
                        type="range"
                        min="0"
                        max="12"
                        step="0.5"
                        value={log.sleepHours}
                        onChange={(e) => updateLog('sleepHours', parseFloat(e.target.value))}
                        className="compact-slider"
                    />
                </div>
            </div>

            {/* Exercise */}
            <div className="quick-input-row" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                    <div className="quick-input-icon"><Dumbbell size={20} /></div>
                    <div className="quick-input-label">Exercise</div>
                </div>
                <div className="chip-group" style={{ marginBottom: 8 }}>
                    {['none', 'resistance', 'cardio', 'yoga', 'walking'].map(t => (
                        <div
                            key={t}
                            className={`chip ${log.exerciseType === t ? 'active' : ''}`}
                            onClick={() => updateLog('exerciseType', t)}
                            style={{ padding: '6px 12px', fontSize: 13 }}
                        >
                            {t === 'none' ? 'None' : t.charAt(0).toUpperCase() + t.slice(1)}
                        </div>
                    ))}
                </div>
                {log.exerciseType !== 'none' && (
                    <div className="quick-input-right" style={{ alignSelf: 'flex-end', width: '100%', justifyContent: 'flex-end' }}>
                        <span className="compact-value">{log.exerciseMinutes} min</span>
                        <input
                            type="range"
                            min="0"
                            max="120"
                            step="5"
                            value={log.exerciseMinutes}
                            onChange={(e) => updateLog('exerciseMinutes', parseInt(e.target.value))}
                            className="compact-slider"
                            style={{ width: '60%' }}
                        />
                    </div>
                )}
            </div>

            {/* Alcohol */}
            <div className="quick-input-row">
                <div className="quick-input-left">
                    <div className="quick-input-icon"><Wine size={20} /></div>
                    <div className="quick-input-label">Alcohol Drinks</div>
                </div>
                <div className="counter compact-counter">
                    <button className="counter-btn" onClick={() => updateLog('alcoholDrinks', Math.max(0, log.alcoholDrinks - 1))}>−</button>
                    <span className="counter-value">{log.alcoholDrinks}</span>
                    <button className="counter-btn" onClick={() => updateLog('alcoholDrinks', Math.min(10, log.alcoholDrinks + 1))}>+</button>
                </div>
            </div>

            {/* Caffeine */}
            <div className="quick-input-row" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                    <div className="quick-input-icon"><Coffee size={20} /></div>
                    <div className="quick-input-label">Caffeine Intake</div>
                </div>
                <div className="chip-group">
                    {['none', 'morning', 'afternoon', 'evening'].map(t => (
                        <div
                            key={t}
                            className={`chip ${log.caffeineTiming === t ? 'active' : ''}`}
                            onClick={() => updateLog('caffeineTiming', t)}
                            style={{ padding: '6px 12px', fontSize: 13 }}
                        >
                            {t === 'none' ? 'None' : t.charAt(0).toUpperCase() + t.slice(1)}
                        </div>
                    ))}
                </div>
            </div>

            {/* UPF & Fiber (Simplified) */}
            <div className="quick-input-row">
                <div className="quick-input-left">
                    <div className="quick-input-icon"><Salad size={20} /></div>
                    <div className="quick-input-label">Veg / Fiber Servings</div>
                </div>
                <div className="counter compact-counter">
                    <button className="counter-btn" onClick={() => updateLog('fiberServings', Math.max(0, log.fiberServings - 1))}>−</button>
                    <span className="counter-value">{log.fiberServings}</span>
                    <button className="counter-btn" onClick={() => updateLog('fiberServings', Math.min(10, log.fiberServings + 1))}>+</button>
                </div>
            </div>

            {/* Actions */}
            <div className="sticky-bottom" style={{ position: 'fixed', bottom: 64, left: 0, right: 0, padding: '16px 20px', background: '#fff', borderTop: '1px solid var(--border-medium)', maxWidth: 480, margin: '0 auto', zIndex: 10 }}>
                <button className="btn-action primary" onClick={handleSave}>
                    Save Daily Log
                </button>
            </div>
        </div>
    );
}
